import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { apiClient } from '../../api/client';
import type {
  ConvertEstimateResult,
  CreateEstimatePayload,
  EstimateDetail,
  EstimateListItem,
  PagedResult,
  PublicEstimate,
  SendEstimateResult,
  UpdateEstimatePayload
} from './types';

interface EstimateListFilters {
  customerId?: string;
  status?: string;
  from?: string;
  to?: string;
  page?: number;
  pageSize?: number;
}

export function useEstimateList(filters: EstimateListFilters = {}) {
  return useQuery({
    queryKey: ['estimates', filters],
    queryFn: async () => {
      const res = await apiClient.get<PagedResult<EstimateListItem>>('/api/v1/estimates', { params: filters });
      return res.data;
    },
    staleTime: 30_000,
    placeholderData: (prev) => prev
  });
}

export function useEstimate(id: string | undefined) {
  return useQuery({
    queryKey: ['estimate', id],
    queryFn: async () => {
      const res = await apiClient.get<EstimateDetail>(`/api/v1/estimates/${id}`);
      return res.data;
    },
    enabled: !!id,
    staleTime: 30_000
  });
}

export function useCreateEstimate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateEstimatePayload) => {
      const res = await apiClient.post<EstimateDetail>('/api/v1/estimates', payload);
      return res.data;
    },
    onSuccess: (detail) => {
      qc.invalidateQueries({ queryKey: ['estimates'] });
      qc.setQueryData(['estimate', detail.id], detail);
    }
  });
}

export function useUpdateEstimate(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: UpdateEstimatePayload) => {
      await apiClient.put(`/api/v1/estimates/${id}`, payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['estimates'] });
      qc.invalidateQueries({ queryKey: ['estimate', id] });
    }
  });
}

export function useSendEstimate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await apiClient.post<SendEstimateResult>(`/api/v1/estimates/${id}/send`);
      return { id, result: res.data };
    },
    onSuccess: ({ id }) => {
      qc.invalidateQueries({ queryKey: ['estimates'] });
      qc.invalidateQueries({ queryKey: ['estimate', id] });
    }
  });
}

export function useExtendValidity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, days }: { id: string; days?: number }) => {
      await apiClient.post(`/api/v1/estimates/${id}/extend-validity`, { days });
      return id;
    },
    onSuccess: (id) => {
      qc.invalidateQueries({ queryKey: ['estimates'] });
      qc.invalidateQueries({ queryKey: ['estimate', id] });
    }
  });
}

export function useMarkAccepted() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.post(`/api/v1/estimates/${id}/mark-accepted`, {});
      return id;
    },
    onSuccess: (id) => {
      qc.invalidateQueries({ queryKey: ['estimates'] });
      qc.invalidateQueries({ queryKey: ['estimate', id] });
    }
  });
}

export function useMarkDeclined() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, notes }: { id: string; notes?: string }) => {
      await apiClient.post(`/api/v1/estimates/${id}/mark-declined`, { notes });
      return id;
    },
    onSuccess: (id) => {
      qc.invalidateQueries({ queryKey: ['estimates'] });
      qc.invalidateQueries({ queryKey: ['estimate', id] });
    }
  });
}

export function useConvertEstimate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, visitId }: { id: string; visitId?: string | null }) => {
      const res = await apiClient.post<ConvertEstimateResult>(`/api/v1/estimates/${id}/convert`, { visitId });
      return { id, result: res.data };
    },
    onSuccess: ({ id }) => {
      qc.invalidateQueries({ queryKey: ['estimates'] });
      qc.invalidateQueries({ queryKey: ['estimate', id] });
      // Conversion creates a Draft receipt — refresh the receipts list too.
      qc.invalidateQueries({ queryKey: ['receipts'] });
    }
  });
}

export function useVoidEstimate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, notes }: { id: string; notes?: string }) => {
      await apiClient.post(`/api/v1/estimates/${id}/void`, { notes });
      return id;
    },
    onSuccess: (id) => {
      qc.invalidateQueries({ queryKey: ['estimates'] });
      qc.invalidateQueries({ queryKey: ['estimate', id] });
    }
  });
}

/** Public estimate fetch — no auth, uses the raw API base URL. */
export function usePublicEstimate(nonce: string | undefined) {
  return useQuery({
    queryKey: ['public-estimate', nonce],
    queryFn: async () => {
      // Separate axios call: the default apiClient attaches a Bearer token via
      // the MSAL ref. The public endpoint must be anonymous.
      const base = import.meta.env.VITE_API_BASE_URL;
      const res = await axios.get<PublicEstimate>(`${base}/api/v1/public/estimates/${nonce}`);
      return res.data;
    },
    enabled: !!nonce,
    staleTime: 60_000,
    retry: false
  });
}
